import React, { Suspense, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import Layout from '../../../ui/Layout'
import Loading from '../../../../splash/components/Loading/index'
import detectMobileBrowser from '../../../../utils/detectMobileBrowser'
import lazyfy from '../../../../helpers/lazyfy'
import LoadingSkeleton from '../../Generics/LoadingSkeleton/index'
import { selectLabel } from '../../../../flux/selectors/i18n'
import ProductDetail from '../ProductDetail'
import ProductHeader from '../ProductHeader'
import ProductCategory from '../ProductCategory'
import ProductMedia from '../ProductMedia/index'
import { SimilarProductsCarouselSkelton } from '../../Products/SimilarProductsCarousel/index'
import { ExpandableDrawerContext } from '../../../ui/contexts/ExpandableDrawerContext'
import Disclaimer from '../../Disclaimer'

const StoreInfo = lazyfy(() => import('../../Stores/StoreInfo'))

const SimilarProductsCarousel = lazyfy(() => import('../../Products/SimilarProductsCarousel'))

const StyledProductWrapper = styled.div(({ theme: { colors } }) => {
  const slideLeftBorder = 3
  const isMobileBrowser = detectMobileBrowser()

  const mobileSlideStyle = {
    border: `${slideLeftBorder}px solid ${colors.light_grey}`,
    borderWidth: `0 ${slideLeftBorder}px`,
    WebkitOverflowScrolling: 'touch',
    overflowY: 'auto',
  }

  return {
    ...((isMobileBrowser) ? mobileSlideStyle : {}),
    background: 'white',
    width: '100%',
    willChange: 'transform',
    overflowX: 'hidden',
  }
})

const StyledSimilarProductsCarousel = styled.div(({ theme: { spaces, breakpoints } }) => ({
  padding: `0 ${spaces.default / 2}px`,

  [`@media (min-width: ${breakpoints.medium})`]: {
    padding: `0px ${spaces.default}px`,
  },
}))

const StyledProductMedia = styled(ProductMedia)((
  { theme: { breakpoints, mediaSizes } },
) => ({
  minHeight: 200,
  maxHeight: mediaSizes.mobile.height,

  [`@media (min-width: ${breakpoints.medium})`]: {
    height: mediaSizes.desktop.height,
    maxHeight: mediaSizes.desktop.height,
  },
}))

const Extended = ({
  productId, onShowAllSimilarProductsCtaClick,
}) => {
  const [contentRef] = React.useContext(ExpandableDrawerContext)
  const [isDrawerVisible, setDrawerVisible] = useState(false)
  const mainRef = React.useRef()
  const disclaimer = useSelector(selectLabel('disclaimer'))

  useEffect(() => {
    setDrawerVisible(!!mainRef?.current)
  }, [mainRef?.current, setDrawerVisible])

  useEffect(() => {
    try {
      contentRef.scrollTo(0, 0)
    // eslint-disable-next-line no-empty
    } catch (e) {}
  }, [productId])

  return (
    <Layout.Main ref={mainRef}>
      <Layout.Wrapper>
        { isDrawerVisible
          ? (
            <>
              <Suspense fallback={(<Loading />)}>
                <>
                  <StyledProductWrapper>
                    <ProductCategory />
                    <ProductHeader productId={productId} />
                    <StyledProductMedia productId={productId} isAutoPlayEnabled />
                    <ProductDetail productId={productId} containerRef={contentRef} />
                  </StyledProductWrapper>
                  <Suspense fallback={<SimilarProductsCarouselSkelton />}>
                    <StyledSimilarProductsCarousel>
                      <SimilarProductsCarousel
                        containerRef={contentRef}
                        similarProductId={productId}
                        onShowAllClick={onShowAllSimilarProductsCtaClick}
                      />
                    </StyledSimilarProductsCarousel>
                  </Suspense>
                </>
              </Suspense>
              <Suspense fallback={(<LoadingSkeleton width="100%" height="100%" />)}>
                <StoreInfo containerRef={contentRef} />
              </Suspense>
              <Disclaimer dataTest="disclaimer" label={disclaimer} />
            </>
          ) : null}
      </Layout.Wrapper>
    </Layout.Main>
  )
}

Extended.propTypes = {
  productId: PropTypes.string,
  onShowAllSimilarProductsCtaClick: PropTypes.func,
}

Extended.defaultProps = {
  productId: '',
  onShowAllSimilarProductsCtaClick: () => false,
}

export default Extended
